import { USD } from '@/lib/constants';
import { chain } from '@/lib/constants/config';
import { errorHandler } from '@/lib/utils/error-handler';
import { useMutation, useQuery } from '@tanstack/react-query';
import { erc20Abi, formatUnits } from 'viem';
import { usePublicClient } from 'wagmi';

export const useBalance = (address: `0x${string}` | undefined) => {
  const publicClient = usePublicClient({ chainId: chain.id });
  const balance = useQuery<number, Error>({
    queryKey: ['/blockchain/balance', address],
    queryFn: async () => {
      try {
        if (!address) throw new Error('Address is required.');
        if (!publicClient) throw new Error('Public client is required.');
        const balance = await publicClient.readContract({
          address: USD.address,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [address],
        });
        return Number(formatUnits(balance, USD.decimals));
      } catch (e: any) {
        errorHandler(e);
      }
    },
    enabled: Boolean(address && publicClient),
    refetchInterval: chain.blockTime * 4,
    staleTime: chain.blockTime * 4,
  });
  return { balance };
};

export const useGetBalance = () => {
  const publicClient = usePublicClient({ chainId: chain.id });
  const getBalance = useMutation<bigint, Error, `0x${string}`>({
    mutationFn: async (address: `0x${string}`) => {
      try {
        if (!publicClient) throw new Error('Public client is required.');
        const balance = await publicClient.getBalance({ address });
        return balance;
      } catch (error: any) {
        errorHandler(error);
      }
    },
  });
  return { getBalance };
};
